import {
  HStack,
  IndicatorBadge,
  IndicatorBadgeVariants,
  InlineFormattedNumber,
  Meter,
  prettyExtendedDuration,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  useCurrency,
  type CustomerNetAcquisitionLimitStatus,
  type ICustomerNetAcquisitionLimit,
} from '@talos/kyoko';
import Big from 'big.js';

interface LimitStatusProps {
  statusData: CustomerNetAcquisitionLimitStatus;
}

function getAcquisitionStatus(statusData: CustomerNetAcquisitionLimitStatus) {
  const limitAmnt = Big(statusData.Limit.RejectThreshold!);
  const acqAmnt = Big(statusData.Status.Amount);

  const remainAmnt = limitAmnt.sub(acqAmnt);

  let acqPct = Big(0);
  let remainPct = Big(1);

  if (!limitAmnt.eq(0)) {
    acqPct = acqAmnt.div(limitAmnt);
    remainPct = remainAmnt.div(limitAmnt);
  }

  return { limitAmnt, acqAmnt, remainAmnt, acqPct, remainPct };
}

function ModeBadge({ limit }: { limit: ICustomerNetAcquisitionLimit }) {
  if (limit.Mode === 'Enabled') {
    return <IndicatorBadge children="Enabled" variant={IndicatorBadgeVariants.Positive} />;
  } else {
    return <IndicatorBadge children="Disabled" variant={IndicatorBadgeVariants.Negative} />;
  }
}

export function LimitStatus({ statusData }: LimitStatusProps) {
  const { acqAmnt, remainAmnt, limitAmnt, acqPct, remainPct } = getAcquisitionStatus(statusData);
  const { Limit: limit } = statusData;
  const amntCurrency = useCurrency(limit.ThresholdAsset);

  return (
    <Stack gap="spacing12">
      <Stack alignItems="stretch" gap="spacing4">
        <HStack justifyContent="space-between" fontSize="fontSizeSmall">
          <HStack gap="spacing4">
            <HStack gap="spacing4">
              <Text>Acquired</Text>
              <InlineFormattedNumber number={acqPct.mul(100).toFixed(2)} currency="%" />
            </HStack>
          </HStack>
          <HStack gap="spacing4">
            <Text>Remaining</Text>
            <InlineFormattedNumber number={remainPct.mul(100).toFixed(2)} currency="%" />
          </HStack>
        </HStack>
        <Meter color="colors.blue.lighten" showLabel={false} value={acqPct.toNumber()} height={18} />
      </Stack>
      <HStack gap="spacing8">
        <Table flex={1}>
          <Tbody>
            <Tr>
              <Th>Limit Name</Th>
              <Td align="right">{limit.Name}</Td>
            </Tr>
            <Tr>
              <Th>Mode</Th>
              <Td align="right">
                <ModeBadge limit={limit} />
              </Td>
            </Tr>
            <Tr>
              <Th>Window Duration</Th>
              <Td align="right">{prettyExtendedDuration(limit.WindowDuration)}</Td>
            </Tr>
          </Tbody>
        </Table>
        <Table flex={1}>
          <Tbody>
            <Tr>
              <Th>Acquired Amount</Th>
              <Td align="right">
                <InlineFormattedNumber
                  number={acqAmnt}
                  increment={amntCurrency?.DefaultIncrement}
                  currency={amntCurrency?.Symbol}
                />
              </Td>
            </Tr>
            <Tr>
              <Th>Limit Amount</Th>
              <Td align="right">
                <InlineFormattedNumber
                  number={limitAmnt}
                  increment={amntCurrency?.DefaultIncrement}
                  currency={amntCurrency?.Symbol}
                />
              </Td>
            </Tr>
            <Tr>
              <Th>Remaining Amount</Th>
              <Td align="right">
                <InlineFormattedNumber
                  number={remainAmnt}
                  increment={amntCurrency?.DefaultIncrement}
                  currency={amntCurrency?.Symbol}
                />
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </HStack>
    </Stack>
  );
}
